import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { listNews, markRead, CATEGORIES } from "../api/news";
import NewsCard from "../components/news/NewsCard";
import CabinetChrome from "../components/cabinet/CabinetChrome";
import CabIcon from "../components/cabinet/CabIcon";
import { CatIcon } from "../components/news/StatIcons";
import { Button, EmptyState, Skeleton, fmtNum, plural } from "../components/ui";
import usePageTitle from "../hooks/usePageTitle";
import "../styles/news.css";

const MIN_USER_ID = 1;

export default function NewsListPage() {
  usePageTitle("Блог МИНа — Рублокс");
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isMin = user?.userId === MIN_USER_ID;

  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);   // догрузка отдельно от первой
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);       // триггер «Повторить»
  const LIMIT = 20;

  /* Загрузка первой страницы.
     Было `.catch(() => {})`: упавший запрос оставлял items пустым, и экран
     показывал «Пока новостей нет 🙂» — то есть УТВЕРЖДАЛ, что блог пуст,
     хотя просто не смог его получить. Плюс не было отмены: быстрое
     переключение рубрик могло дорисовать статьи одной рубрики под
     заголовком другой. */
  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true);
    setError(false);
    listNews({ category, page: 1, limit: LIMIT }, { signal: ctl.signal })
      .then((d) => {
        if (ctl.signal.aborted) return;
        setItems(d.data || []);
        setTotal(d.total || 0);
        setPage(1);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setError(true);
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });
    return () => ctl.abort();
  }, [category, tick]);

  // Отметить раздел прочитанным при открытии
  useEffect(() => {
    /* Локальную метку ставим ТОЛЬКО после успешного ответа сервера.
       Раньше она писалась безусловно: при неудачном markRead бейдж
       «новое» пропадал локально, хотя сервер считал новости
       непрочитанными — состояния расходились без шанса это заметить. */
    markRead()
      .then(() => {
        try {
          localStorage.setItem("nws_last_read", new Date().toISOString());
        } catch {}
      })
      .catch(() => {});
  }, []);

  /* Догрузка. Раньше кнопка не блокировалась: два клика подряд считали
     `next` от одного и того же `page` и грузили страницу 2 дважды —
     в списке появлялись дубли. */
  function loadMore() {
    if (more || loading) return;
    const next = page + 1;
    setMore(true);
    listNews({ category, page: next, limit: LIMIT })
      .then((d) => {
        setItems((prev) => [...prev, ...(d.data || [])]);
        setPage(next);
      })
      .catch(() => setError(true))
      .finally(() => setMore(false));
  }

  return (
    <CabinetChrome>
    <div className="nws-page">
      {/* Топбар с «← В кабинет» нужен только гостям (у них нет меню слева).
          Авторизованным навигацию даёт меню кабинета (CabinetChrome). */}
      {!isAuthenticated && (
        <header className="nws-topbar">
          <div className="nws-topbar-inner">
            <Link to="/app" className="nws-back">← В кабинет</Link>
            <strong style={{ fontSize: 16 }}>Блог МИНа</strong>
          </div>
        </header>
      )}

      <div className="nws-container">
        <div className="nws-head">
          <h1>Блог МИНа</h1>
          <div className="nws-sub">Новости разработки Рублокса, обновления и мысли</div>
        </div>

        {/* Кнопки, а не <span onClick>: спаны не получают фокус, их нельзя
            выбрать с клавиатуры и не читает скринридер. И SVG-иконка
            рубрики вместо эмодзи — та же CatIcon, что уже используется
            в карточке и в статье (было два представления одной сущности). */}
        <div className="nws-cats" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!category}
            className={`nws-cat ${!category ? "is-active" : ""}`}
            onClick={() => setCategory(null)}
          >
            Все
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={category === c.id}
              className={`nws-cat ${category === c.id ? "is-active" : ""}`}
              onClick={() => setCategory(c.id)}
            >
              <CatIcon id={c.id} /> {c.label}
            </button>
          ))}
        </div>

        {error && items.length === 0 ? (
          <EmptyState
            title="Новости не загрузились"
            desc="Проверь соединение и попробуй ещё раз."
            action={
              <Button variant="primary" onClick={() => setTick((t) => t + 1)}>
                Повторить
              </Button>
            }
          />
        ) : loading ? (
          <div className="nws-grid">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} h={190} r="var(--r-lg)" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={category ? "В этой рубрике пусто" : "Новостей пока нет"}
            desc={
              category
                ? "Загляни в другие рубрики — там наверняка что-то есть."
                : "Здесь появятся новости разработки Рублокса."
            }
            action={
              category ? (
                <Button variant="ghost" onClick={() => setCategory(null)}>
                  Все рубрики
                </Button>
              ) : isMin ? (
                <Button variant="primary" onClick={() => navigate("/app/news/new")}>
                  Написать новость
                </Button>
              ) : null
            }
          />
        ) : (
          <>
            <div className="nws-grid">
              {items.map((n) => <NewsCard key={n.id} news={n} />)}
            </div>
            {/* Ошибка догрузки: список остаётся, сообщение рядом. */}
            {error && (
              <div className="nws-loadmore-error">
                Не удалось загрузить продолжение.
              </div>
            )}
            {page * LIMIT < total && (
              <div style={{ textAlign: "center", marginTop: 24 }}>
                <Button variant="ghost" onClick={loadMore} loading={more}>
                  Показать ещё {fmtNum(total - items.length)}{" "}
                  {plural(total - items.length, "новость", "новости", "новостей")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {isMin && (
        <button className="nws-fab" title="Новая новость" aria-label="Новая новость"
                onClick={() => navigate("/app/news/new")}>
          <CabIcon name="plus" size={22} />
        </button>
      )}
    </div>
    </CabinetChrome>
  );
}
